import { Component, EventEmitter, Output, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppButton } from '../../../../../../shared/components/app-button/app-button';
import { SettingsService } from '../../../../../../core/services/settings.service';
import { AuthService } from '../../../../../../core/services/auth.service';
import { firstValueFrom } from 'rxjs';
import { toast } from 'ngx-sonner';
import * as Papa from 'papaparse';

import { Dialog } from 'primeng/dialog';
import { SharedModule } from 'primeng/api';

import * as Ontology from '../../../../../../core/models/diagnocat-ontology';

interface CSVPreviewRow {
  code: string;
  label: string;
  rootCode?: string;
  rootLabel?: string;
  changes: any;
  isValid: boolean;
  error?: string;
  ontologyId?: string;
  raw: Record<string, string>;
}

@Component({
  selector: 'app-dtp-import-rules-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule, AppButton, Dialog, SharedModule],
  template: `
    <p-dialog
      [visible]="true"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      styleClass="!rounded-[2rem] overflow-hidden shadow-2xl w-full max-w-[95vw]"
      (onHide)="onClose()"
      [style]="{ width: '1200px', 'max-height': '95vh' }"
    >
      <ng-template pTemplate="header">
        <div class="flex items-center justify-between w-full pr-8">
          <div class="flex items-center gap-4">
            <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center border border-primary/10">
              <i class="pi pi-table text-lg text-primary"></i>
            </div>
            <div>
              <h3 class="text-lg font-bold text-slate-800 tracking-tight">{{ 'BULK_IMPORT.TITLE' | translate }}</h3>
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{{ 'BULK_IMPORT.SUBTITLE' | translate }}</p>
            </div>
          </div>

          <button (click)="downloadTemplate()" class="flex items-center gap-2 text-[10px] font-bold text-primary bg-primary/5 hover:bg-primary/10 px-3 py-1.5 rounded-lg transition-all uppercase tracking-widest border border-primary/10 mr-4">
            <i class="pi pi-download"></i>
            {{ 'BULK_IMPORT.DOWNLOAD_TEMPLATE' | translate }}
          </button>
        </div>
      </ng-template>

      <!-- Content -->
      <div class="flex flex-col w-full py-2">
        <!-- Step 1: Upload -->
        <div *ngIf="!previewData.length" class="flex flex-col items-center justify-center py-20">
          <div class="w-full max-w-2xl p-16 border-2 border-dashed rounded-[2.5rem] transition-all duration-300 cursor-pointer relative flex flex-col items-center text-center group"
               [ngClass]="isDragging ? 'border-primary bg-primary/5 scale-[1.01] shadow-xl shadow-primary/10' : 'border-slate-200 bg-slate-50 hover:border-primary/40 hover:bg-white hover:shadow-sm'"
               (click)="fileInput.click()"
               (dragover)="onDragOver($event)"
               (dragleave)="onDragLeave($event)"
               (drop)="onDrop($event)">
            <input type="file" #fileInput (change)="onFileSelected($event)" accept=".csv" class="hidden">

            <div class="w-24 h-24 rounded-[2.5rem] bg-white shadow-sm border border-slate-100 flex items-center justify-center mb-6 transition-all duration-500"
                 [class.scale-110]="isDragging"
                 [class.-translate-y-2]="isDragging">
              <i class="pi pi-file-excel text-emerald-500" style="font-size: 3rem"></i>
            </div>
            <h4 class="text-2xl font-bold text-slate-800 mb-2">{{ isDragging ? ('BULK_IMPORT.DROP_TO_UPLOAD' | translate) : ('BULK_IMPORT.UPLOAD_CSV' | translate) }}</h4>
            <p class="text-sm font-medium text-slate-400 max-w-md mb-8 leading-relaxed">
              {{ 'BULK_IMPORT.UPLOAD_HINT' | translate }}
            </p>

            <app-button [label]="isDragging ? ('BULK_IMPORT.RELEASE_FILE' | translate) : ('BULK_IMPORT.BROWSE_FILES' | translate)" icon="pi pi-search" [variant]="isDragging ? 'primary' : 'outline'"></app-button>
          </div>
        </div>

        <!-- Step 2: Spreadsheet Preview -->
        <div *ngIf="previewData.length" class="space-y-4 px-2">
          <div class="flex items-center justify-between mb-2 bg-slate-50 p-4 rounded-2xl border border-slate-100">
            <div class="flex items-center gap-4">
              <div class="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 text-white text-[11px] font-bold uppercase tracking-wider shadow-sm shadow-emerald-200">
                <i class="pi pi-check-circle"></i> {{ validCount }} {{ 'BULK_IMPORT.VALID' | translate }}
              </div>
              <div *ngIf="invalidCount > 0" class="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-500 text-white text-[11px] font-bold uppercase tracking-wider shadow-sm shadow-rose-200">
                <i class="pi pi-exclamation-circle"></i> {{ invalidCount }} {{ 'BULK_IMPORT.INVALID' | translate }}
              </div>
              <div class="h-6 w-[1px] bg-slate-200 mx-2"></div>
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{{ 'BULK_IMPORT.TOTAL_ROWS' | translate }}: {{ previewData.length }}</p>
            </div>
            <button (click)="clearPreview()" class="flex items-center gap-2 text-[11px] font-bold text-slate-400 hover:text-rose-500 transition-colors uppercase tracking-widest px-4 py-2 rounded-xl hover:bg-rose-50 border border-transparent hover:border-rose-100">
              <i class="pi pi-refresh"></i> {{ 'BULK_IMPORT.RESET_IMPORT' | translate }}
            </button>
          </div>

          <!-- Spreadsheet Grid -->
          <div class="border border-slate-100 rounded-2xl overflow-hidden shadow-sm bg-white">
            <div class="overflow-x-auto overflow-y-auto max-h-[55vh] custom-scrollbar">
              <table class="w-full text-left border-collapse min-w-[1800px]">
                <thead class="sticky top-0 z-20">
                  <tr class="bg-slate-800 text-white border-b border-slate-700">
                    <th class="px-4 py-3 text-[9px] font-black uppercase tracking-widest sticky left-0 z-30 bg-slate-800 border-r border-slate-700 w-40">{{ 'BULK_IMPORT.STATUS' | translate }}</th>
                    <th *ngFor="let h of csvHeaders" class="px-4 py-3 text-[9px] font-black uppercase tracking-widest border-r border-slate-700 min-w-[110px]">{{ formatHeader(h) }}</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  <tr *ngFor="let row of previewData" class="transition-colors group"
                      [ngClass]="row.isValid ? 'bg-emerald-50/40 hover:bg-emerald-50' : 'bg-rose-50/40 hover:bg-rose-50'">

                    <!-- Sticky Status Column -->
                    <td class="px-4 py-3 sticky left-0 z-10 border-r border-slate-100 shadow-[2px_0_5px_rgba(0,0,0,0.03)]"
                        [ngClass]="row.isValid ? 'bg-emerald-50 group-hover:bg-emerald-100/60' : 'bg-rose-50 group-hover:bg-rose-100/60'">
                      <div class="flex items-center gap-2">
                        <div class="w-6 h-6 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0"
                             [ngClass]="row.isValid ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'">
                          <i class="pi text-[8px]" [ngClass]="row.isValid ? 'pi-check' : 'pi-times'"></i>
                        </div>
                        <div class="flex flex-col min-w-0">
                          <span class="text-[10px] font-black truncate" [ngClass]="row.isValid ? 'text-emerald-700' : 'text-rose-700'">
                            {{ row.isValid ? ('BULK_IMPORT.ROW_VALID' | translate) : ('BULK_IMPORT.ROW_REJECTED' | translate) }}
                          </span>
                          <span *ngIf="!row.isValid" class="text-[8px] font-bold text-rose-400 truncate" [title]="row.error || ''">{{ row.error }}</span>
                        </div>
                      </div>
                    </td>

                    <!-- Dynamic CSV Data Columns -->
                    <td *ngFor="let h of csvHeaders" class="px-4 py-3 text-[11px] border-r border-slate-50 last:border-r-0"
                        [ngClass]="row.isValid ? 'text-slate-700' : 'text-rose-400/70'">
                      <span class="font-medium whitespace-nowrap">{{ row.raw[h] || '' }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex items-center justify-between w-full pt-4 px-2">
          <div class="text-slate-400 text-[10px] font-bold flex items-center gap-2 uppercase tracking-widest" *ngIf="previewData.length">
            <i class="pi pi-shield-check text-emerald-500"></i>
            {{ 'BULK_IMPORT.FOOTER_HINT' | translate }}
          </div>
          <div *ngIf="!previewData.length"></div>
          <div class="flex gap-3">
            <app-button [label]="'BULK_IMPORT.CANCEL' | translate" type="secondary" variant="ghost" (onClick)="onClose()"></app-button>
            <app-button *ngIf="previewData.length"
                        [label]="'BULK_IMPORT.COMMIT_DATA' | translate"
                        icon="pi pi-cloud-upload"
                        [disabled]="validCount === 0 || isSaving"
                        [loading]="isSaving"
                        (onClick)="commitImport()"></app-button>
          </div>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    :host { display: block; }
    .custom-scrollbar::-webkit-scrollbar { height: 8px; width: 8px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
  `]
})
export class DtpImportRulesModal {
  @Output() close = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  private settingsService = inject(SettingsService);
  private authService = inject(AuthService);
  private translateService = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  previewData: CSVPreviewRow[] = [];
  csvHeaders: string[] = [];
  isSaving = false;
  isDragging = false;
  validCount = 0;
  invalidCount = 0;

  onClose() { this.close.emit(); }

  formatHeader(h: string): string {
    return h.replace(/_/g, ' ');
  }

  // ── Drag & Drop ──────────────────────────────────────────────────────

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.csv')) {
        this.handleFile(file);
      } else {
        toast.error(this.translateService.instant('BULK_IMPORT.ERROR_ONLY_CSV'));
      }
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) this.handleFile(file);
  }

  // ── CSV Parsing with PapaParse ───────────────────────────────────────

  private handleFile(file: File) {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header: string) => header.trim().toLowerCase(),
      complete: async (results: Papa.ParseResult<Record<string, string>>) => {
        if (results.errors.length > 0) {
          console.warn('PapaParse warnings:', results.errors);
        }
        await this.processRows(results.meta.fields || [], results.data);
      },
      error: (err: any) => {
        console.error('PapaParse error:', err);
        toast.error(this.translateService.instant('BULK_IMPORT.ERROR_PARSE'));
      }
    });
  }

  private async processRows(headers: string[], rows: Record<string, string>[]) {
    const companyId = (this.authService as any).userProfile?.['company_id'];
    if (!companyId) {
      toast.error(this.translateService.instant('BULK_IMPORT.COMPANY_NOT_FOUND'));
      return;
    }

    if (rows.length === 0) {
      toast.error(this.translateService.instant('BULK_IMPORT.ERROR_EMPTY'));
      return;
    }

    this.csvHeaders = headers;

    // Load all ontology rules for matching
    const allRules = await firstValueFrom(
      this.settingsService.getOntologyRulesWithOverrides(companyId)
    ) as any[];

    this.previewData = rows.map(rowData => {
      // Match by code or label against ontology
      const code = (rowData['code'] || '').trim();
      const label = (rowData['label'] || '').trim();

      const rule = allRules.find((r: any) => {
        if (code && r.code === code) return true;
        if (label && r.label?.toLowerCase() === label.toLowerCase()) return true;
        return false;
      });

      // Build changes object from the override fields
      const changes: any = {};
      const overrideFields = [
        'domain', 'crown_state', 'root_state', 'render_asset',
        'color_rule', 'approval_rule', 'recommended_approval',
        'default_severity', 'treatment_relevant', 'monitoring_relevant',
        'priority_rank'
      ];

      let enumError: string | undefined;

      for (const f of overrideFields) {
        const rawVal = (rowData[f] || '').trim();
        if (!rawVal || rawVal.toLowerCase() === 'null') continue;

        let val: any = rawVal;

        // Validate enums
        if (!enumError) {
          if (f === 'domain' && !(Ontology.DIAGNOCAT_DOMAINS as readonly string[]).includes(val)) {
            enumError = this.translateService.instant('BULK_IMPORT.ERROR_INVALID_DOMAIN') + val;
          }
          if (f === 'crown_state' && !(Ontology.TOOTH_CROWN_STATES as readonly string[]).includes(val)) {
            enumError = this.translateService.instant('BULK_IMPORT.ERROR_INVALID_CROWN') + val;
          }
          if (f === 'root_state' && !(Ontology.TOOTH_ROOT_STATES as readonly string[]).includes(val)) {
            enumError = this.translateService.instant('BULK_IMPORT.ERROR_INVALID_ROOT') + val;
          }
          if (f === 'render_asset' && !(Ontology.RENDER_ASSET_TYPES as readonly string[]).includes(val)) {
            enumError = this.translateService.instant('BULK_IMPORT.ERROR_INVALID_ASSET') + val;
          }
          if (f === 'color_rule' && !(Ontology.COLOR_RULES as readonly string[]).includes(val)) {
            enumError = this.translateService.instant('BULK_IMPORT.ERROR_INVALID_COLOR') + val;
          }
          if (f === 'recommended_approval' && !(Ontology.RECOMMENDED_APPROVAL_TYPES as readonly string[]).includes(val)) {
            enumError = this.translateService.instant('BULK_IMPORT.ERROR_INVALID_APPROVAL') + val;
          }
          if (f === 'default_severity' && !(Ontology.SEVERITY_LEVELS as readonly string[]).includes(val)) {
            enumError = this.translateService.instant('BULK_IMPORT.ERROR_INVALID_SEVERITY') + val;
          }
        }

        // Convert booleans
        if (f === 'treatment_relevant' || f === 'monitoring_relevant') {
          val = val.toUpperCase() === 'TRUE';
        }

        // Convert priority_rank to number
        if (f === 'priority_rank') {
          const num = parseInt(val, 10);
          val = isNaN(num) ? val : num;
        }

        changes[f] = val;
      }

      const isValid = !!rule && !enumError;

      return {
        code: code || 'N/A',
        label: label || 'N/A',
        rootCode: (rowData['root_code'] || '').trim(),
        rootLabel: (rowData['root_label'] || '').trim(),
        changes,
        isValid,
        error: enumError || (rule ? undefined : this.translateService.instant('BULK_IMPORT.ERROR_ONTOLOGY_NOT_FOUND')),
        ontologyId: rule?.original_rule?.id || rule?.id,
        raw: rowData
      };
    });

    this.validCount = this.previewData.filter(r => r.isValid).length;
    this.invalidCount = this.previewData.length - this.validCount;
    this.cdr.detectChanges();
  }

  // ── Actions ──────────────────────────────────────────────────────────

  clearPreview() {
    this.previewData = [];
    this.csvHeaders = [];
    this.validCount = 0;
    this.invalidCount = 0;
    this.cdr.detectChanges();
  }

  async commitImport() {
    const companyId = (this.authService as any).userProfile?.['company_id'];
    if (!companyId) return;

    this.isSaving = true;
    const updatedAt = new Date().toISOString();

    const payloads = this.previewData
      .filter(row => row.isValid && row.ontologyId)
      .map(row => ({
        company_id: companyId,
        ontology_id: row.ontologyId,
        ...row.changes,
        updated_at: updatedAt
      }));

    // Deduplicate by ontology_id — last row in CSV wins
    const deduped = new Map<string, any>();
    for (const p of payloads) {
      deduped.set(p.ontology_id, p);
    }
    const uniquePayloads = Array.from(deduped.values());

    if (uniquePayloads.length === 0) {
      this.isSaving = false;
      return;
    }

    try {
      await firstValueFrom(this.settingsService.upsertOntologyOverrides(uniquePayloads));
      toast.success(this.translateService.instant('BULK_IMPORT.SUCCESS_MSG', { count: uniquePayloads.length }));
      this.saved.emit();
      this.onClose();
    } catch (err) {
      console.error('Bulk import error:', err);
      toast.error(this.translateService.instant('BULK_IMPORT.ERROR_CRITICAL'));
    } finally {
      this.isSaving = false;
      this.cdr.detectChanges();
    }
  }

  downloadTemplate() {
    window.open('https://pkqviipqnvoaxmahlblq.supabase.co/storage/v1/object/public/diagnocat/diagnocat_odontogram_code_rules_template.csv', '_blank');
  }
}
